import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/runtime';
import { categoryLabel } from '../i18n/categoryNames';
import { MessageCircle, Clock, RefreshCcw, Star, ChevronRight, Tag } from 'lucide-react';
import ReviewList from '../components/ReviewList';
import { viewCountQueryParam, markViewed } from '../utils/viewTracking';

interface Service {
  id: number;
  user_id: number;
  category_id: number;
  category_name: string;
  title: string;
  description: string;
  short_description: string;
  price_basic: number;
  price_standard?: number;
  price_premium?: number;
  delivery_days_basic: number;
  delivery_days_standard?: number;
  delivery_days_premium?: number;
  revisions_basic: number;
  revisions_standard?: number;
  revisions_premium?: number;
  thumbnail_image?: string;
  portfolio_images?: string[];
  service_includes?: string[];
  skills?: string[];
  tags?: string[];
  faq?: Array<{ question: string; answer: string }>;
  rating_average: number;
  views_count: number;
  orders_count: number;
  seller_nickname: string;
  freelancer_nickname?: string;
  seller_profile_image?: string;
  freelancer_profile_image?: string;
  seller_id?: number;
}

const INDIGO = '#4F46E5';

const card: React.CSSProperties = {
  background: '#fff', borderRadius: 14,
  border: '1px solid #F0F0F0', padding: '22px 24px', marginBottom: 16,
};

const ServiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPackage, setSelectedPackage] = useState<'basic' | 'standard' | 'premium'>('basic');
  const [chatLoading, setChatLoading] = useState(false);

  const fetchServiceDetail = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/services/${id}${viewCountQueryParam('service', id!)}`);
      const result = await res.json();
      if (result.success) {
        setService(result.data);
        markViewed('service', id!);
      } else {
        setError(result.message || t('serviceDetail.loadFailed'));
      }
    } catch (err) {
      console.error('[ServiceDetail] 서비스 상세 조회 오류:', err);
      setError(t('serviceDetail.loadError'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { if (id) fetchServiceDetail(); }, [id, fetchServiceDetail]);

  const getVal = (basic: any, std: any, prem: any) => {
    if (selectedPackage === 'standard') return std || basic;
    if (selectedPackage === 'premium') return prem || basic;
    return basic;
  };

  const handleContact = async () => {
    console.log('[ServiceDetail] 상담 요청 클릭');
    if (!user) { alert(t('common.loginRequired')); navigate('/login'); return; }
    const ownerId = service?.user_id || service?.seller_id;
    if (user.id === ownerId) { alert(t('serviceDetail.ownService')); return; }
    try {
      setChatLoading(true);
      const res = await fetch(`${API_BASE_URL}/chat/room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ serviceId: id })
      });
      const result = await res.json();
      console.log('[ServiceDetail] 채팅방 생성 응답:', result);
      if (result.success) navigate(`/chat/${result.room.id}`);
      else alert(result.message || t('serviceDetail.chatCreateFailed'));
    } catch (err) {
      console.error('[ServiceDetail] 채팅 시작 오류:', err);
      alert(t('serviceDetail.chatCreateError'));
    } finally {
      setChatLoading(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', color: '#BBB', fontSize: 14 }}>
      {t('common.loading')}
    </div>
  );

  if (error || !service) return (
    <div style={{ maxWidth: 600, margin: '60px auto', textAlign: 'center', color: '#666' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>😕</div>
      <h2 style={{ fontSize: 20, fontWeight: 700 }}>{t('serviceDetail.notFound')}</h2>
      <p style={{ color: '#AAA', marginBottom: 20 }}>{error}</p>
      <button onClick={() => navigate('/services')} style={{
        padding: '10px 24px', background: INDIGO, color: '#fff',
        border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
      }}>{t('serviceDetail.backToList')}</button>
    </div>
  );

  const sellerName = service.seller_nickname || service.freelancer_nickname || t('serviceDetail.expert');
  const sellerImg = service.seller_profile_image || service.freelancer_profile_image;
  const sellerId = service.seller_id || service.user_id;

  const pkgPrice    = getVal(service.price_basic, service.price_standard, service.price_premium);
  const pkgDays     = getVal(service.delivery_days_basic, service.delivery_days_standard, service.delivery_days_premium);
  const pkgRev      = getVal(service.revisions_basic, service.revisions_standard, service.revisions_premium);

  return (
    <div style={{ background: '#F8F8FF', minHeight: '100vh', padding: '32px 20px' }}>
      <div style={{ maxWidth: 1060, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24, alignItems: 'start' }}>

        {/* ── 왼쪽 ── */}
        <div>
          {/* 브레드크럼 */}
          <div style={{ fontSize: 13, color: '#AAA', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span onClick={() => navigate('/services')} style={{ cursor: 'pointer', color: INDIGO }}>{t('serviceDetail.breadcrumb')}</span>
            <ChevronRight size={13} color="#CCC" />
            <span>{categoryLabel(service.category_name)}</span>
          </div>

          {/* 제목 */}
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#111', letterSpacing: '-0.7px', marginBottom: 18, lineHeight: 1.3 }}>
            {service.title}
          </h1>

          {/* 전문가 정보 */}
          <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}
            onClick={() => navigate(`/freelancer/${sellerName}`)}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%', background: INDIGO,
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700, flexShrink: 0, overflow: 'hidden',
            }}>
              {sellerImg
                ? <img src={sellerImg} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : sellerName.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>{sellerName}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                <Star size={13} fill="#F59E0B" stroke="#F59E0B" />
                <span style={{ fontSize: 13, color: '#666' }}>
                  {Number(service.rating_average || 0).toFixed(1)} · {t('serviceDetail.ordersN', { n: service.orders_count })}
                </span>
              </div>
            </div>
            <ChevronRight size={16} color="#CCC" />
          </div>

          {/* 썸네일 */}
          {service.thumbnail_image && (
            <div style={{ ...card, padding: 0, overflow: 'hidden', marginBottom: 16 }}>
              <img src={service.thumbnail_image} alt={service.title}
                style={{ width: '100%', maxHeight: 360, objectFit: 'cover' }} />
            </div>
          )}

          {/* 서비스 설명 */}
          <div style={card}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 12 }}>{t('serviceDetail.description')}</h2>
            <p style={{ fontSize: 14, color: '#555', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{service.description}</p>
          </div>

          {/* 스킬 */}
          {service.skills && service.skills.length > 0 && (
            <div style={card}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 12 }}>{t('serviceDetail.skills')}</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {service.skills.map((s, i) => (
                  <span key={i} style={{
                    padding: '5px 12px', background: '#EEF2FF', color: INDIGO,
                    borderRadius: 100, fontSize: 13, fontWeight: 600,
                  }}>{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* 태그 */}
          {service.tags && service.tags.length > 0 && (
            <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Tag size={14} color="#BBB" />
              {service.tags.map((t, i) => (
                <span key={i} style={{ fontSize: 13, color: '#888', cursor: 'pointer' }}
                  onClick={() => navigate(`/services?tag=${encodeURIComponent(t)}`)}>
                  #{t}
                </span>
              ))}
            </div>
          )}

          {/* 포함 내용 */}
          {service.service_includes && service.service_includes.length > 0 && (
            <div style={card}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 12 }}>{t('serviceDetail.includes')}</h2>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {service.service_includes.map((item, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#444' }}>
                    <span style={{ color: '#10B981', flexShrink: 0 }}>✓</span>{item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 포트폴리오 이미지 */}
          {service.portfolio_images && service.portfolio_images.length > 0 && (
            <div style={card}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 12 }}>{t('serviceDetail.portfolio')}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {service.portfolio_images.map((img, i) => (
                  <img key={i} src={img} alt={`${t('serviceDetail.portfolio')} ${i + 1}`}
                    style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                    onClick={() => window.open(img, '_blank')}
                  />
                ))}
              </div>
            </div>
          )}

          {/* FAQ */}
          {service.faq && service.faq.length > 0 && (
            <div style={card}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 14 }}>{t('serviceDetail.faq')}</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {service.faq.map((item, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111', marginBottom: 4 }}>Q. {item.question}</div>
                    <div style={{ fontSize: 14, color: '#666', lineHeight: 1.6 }}>A. {item.answer}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 리뷰 */}
          <div style={card}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 16 }}>{t('serviceDetail.reviews')}</h2>
            <ReviewList userId={sellerId} />
          </div>
        </div>

        {/* ── 오른쪽 패키지 카드 ── */}
        <div style={{ position: 'sticky', top: 80 }}>
          <div style={card}>
            {/* 패키지 탭 */}
            <div style={{ display: 'flex', background: '#F5F5F5', borderRadius: 10, padding: 3, marginBottom: 18 }}>
              {(['basic', service.price_standard ? 'standard' : null, service.price_premium ? 'premium' : null] as any[])
                .filter(Boolean)
                .map((pkg: string) => {
                  const labels: Record<string, string> = { basic: t('serviceDetail.pkgBasic'), standard: t('serviceDetail.pkgStandard'), premium: t('serviceDetail.pkgPremium') };
                  return (
                    <button key={pkg} onClick={() => setSelectedPackage(pkg as 'basic' | 'standard' | 'premium')} style={{
                      flex: 1, padding: '8px 0', border: 'none', borderRadius: 8, cursor: 'pointer',
                      fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                      background: selectedPackage === pkg ? '#fff' : 'transparent',
                      color: selectedPackage === pkg ? INDIGO : '#999',
                      boxShadow: selectedPackage === pkg ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                      transition: 'all 0.15s',
                    }}>{labels[pkg]}</button>
                  );
                })}
            </div>

            {/* 가격 */}
            <div style={{ fontSize: 28, fontWeight: 800, color: '#111', letterSpacing: '-0.7px', marginBottom: 4 }}>
              {t('serviceDetail.priceFrom', { price: Number(pkgPrice).toLocaleString() })}
            </div>
            <div style={{ fontSize: 12, color: '#AAA', marginBottom: 18 }}>{t('serviceDetail.priceNote')}</div>

            {/* 패키지 정보 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20, padding: '14px 0', borderTop: '1px solid #F5F5F5', borderBottom: '1px solid #F5F5F5' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#777' }}>
                  <Clock size={14} />{t('serviceDetail.delivery')}
                </div>
                <strong style={{ color: '#111' }}>{t('serviceDetail.daysFrom', { n: pkgDays })}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#777' }}>
                  <RefreshCcw size={14} />{t('serviceDetail.revisions')}
                </div>
                <strong style={{ color: '#111' }}>{t('serviceDetail.timesN', { n: pkgRev })}</strong>
              </div>
            </div>

            {/* 상담 버튼 */}
            <button onClick={handleContact} disabled={chatLoading} style={{
              width: '100%', padding: '14px',
              background: chatLoading ? '#A5B4FC' : INDIGO, color: '#fff',
              border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700,
              cursor: chatLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
              transition: 'background 0.15s',
            }}>
              <MessageCircle size={16} />
              {chatLoading ? t('serviceDetail.creatingChat') : t('serviceDetail.contactExpert')}
            </button>

            {/* 이용 안내 */}
            <div style={{ marginTop: 18, padding: '14px', background: '#F8F8FF', borderRadius: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111', marginBottom: 10 }}>{t('serviceDetail.howTo')}</div>
              {[t('serviceDetail.step1'), t('serviceDetail.step2'), t('serviceDetail.step3'), t('serviceDetail.step4')].map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: INDIGO, color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                  <span style={{ fontSize: 12, color: '#666', lineHeight: 1.5 }}>{s}</span>
                </div>
              ))}
            </div>

            {/* 통계 */}
            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 14, paddingTop: 14, borderTop: '1px solid #F5F5F5' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#111' }}>{service.views_count}</div>
                <div style={{ fontSize: 11, color: '#BBB' }}>{t('serviceDetail.statViews')}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#111' }}>{service.orders_count}</div>
                <div style={{ fontSize: 11, color: '#BBB' }}>{t('serviceDetail.statOrders')}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#111' }}>{Number(service.rating_average || 0).toFixed(1)}</div>
                <div style={{ fontSize: 11, color: '#BBB' }}>{t('serviceDetail.statRating')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailPage;
